import { cn } from "@/utils";
import { t } from "@lingui/core/macro";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router";
import { useSidebarStore } from "@/stores/useSidebarStore";
import {
  buildNotificationActionLink,
  NotificationAction,
  NotificationItem,
} from "../services/notifications";

type Props = {
  notification: Pick<
    NotificationItem,
    "title" | "message" | "isRead" | "actions"
  >;
  className?: string;
  onAfterNavigate?: () => void;
};

/**
 * Displays a server-rendered notification message + a row of clickable
 * action chips (calendar, customer and freeze-request shortcuts). The localized text
 * is produced server-side; this component is presentation-only.
 */
export function NotificationMessage({
  notification,
  className,
  onAfterNavigate,
}: Props) {
  const navigate = useNavigate();
  const actions = notification.actions ?? [];

  const { isOpen: isSidebarOpen, toggle: toggleSidebar } = useSidebarStore(
    (state) => state,
  );

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <p
        className={cn("text-xs leading-relaxed whitespace-pre-line", {
          "text-default-600": !notification.isRead,
          "text-default-500": notification.isRead,
        })}
      >
        {notification.message}
      </p>

      {actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {actions.map((action, index) => (
            <button
              key={`${action.kind}-${index}`}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                navigate(buildNotificationActionLink(action));
                onAfterNavigate?.();

                if (isSidebarOpen) {
                  toggleSidebar(false);
                }
              }}
              className="bg-primary-50 text-primary hover:bg-primary-100 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
              aria-label={actionAriaLabel(action)}
            >
              {action.label}
              <ExternalLink size={11} aria-hidden className="shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function actionAriaLabel(action: NotificationAction): string {
  return action.kind === "calendar_link"
    ? t`Odpri ${action.label} v koledarju`
    : action.label;
}
